/**
 * Algo Strategies API service.
 *
 * `listActive()` backs the authenticated Strategies page inside this app.
 * The backend endpoint itself requires no authentication (the public
 * marketing site also calls it), but reusing the shared `api` client is
 * still correct — it just attaches the current user's token, which the
 * endpoint ignores.
 *
 * The `admin.*` methods back the Strategy Management admin page
 * (`/admin/strategies`) and call the `/algo/admin/*` sub-router, which
 * requires an ADMIN-role bearer token (enforced server-side; the shared
 * `api` client already attaches it).
 */

import api from "@/services/api";
import type { AlgoCreate, AlgoListResponse, AlgoUpdate, Algo } from "@/types";

export const algoService = {
  async listActive(): Promise<AlgoListResponse> {
    const { data } = await api.get<AlgoListResponse>("/algo");
    return data;
  },

  admin: {
    async listAll(): Promise<AlgoListResponse> {
      const { data } = await api.get<AlgoListResponse>("/algo/admin");
      return data;
    },

    async getById(algoId: string): Promise<Algo> {
      const { data } = await api.get<Algo>(`/algo/admin/${algoId}`);
      return data;
    },

    async create(payload: AlgoCreate): Promise<Algo> {
      const { data } = await api.post<Algo>("/algo/admin", payload);
      return data;
    },

    async update(algoId: string, payload: AlgoUpdate): Promise<Algo> {
      const { data } = await api.patch<Algo>(`/algo/admin/${algoId}`, payload);
      return data;
    },

    async remove(algoId: string): Promise<void> {
      await api.delete(`/algo/admin/${algoId}`);
    },

    async reorder(orderedIds: string[]): Promise<AlgoListResponse> {
      const { data } = await api.post<AlgoListResponse>("/algo/admin/reorder", {
        ordered_ids: orderedIds,
      });
      return data;
    },
  },
};

export default algoService;
